import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const tabsListVariants = cva('', {
  variants: {
    variant: {
      default:
        'bg-muted text-muted-foreground inline-flex h-9 items-center justify-center rounded-lg p-1',
      boxed:
        'no-scrollbar flex w-full flex-nowrap gap-4 overflow-x-scroll will-change-scroll',
    },
    // `horizontal` is a no-op so omitting `orientation` yields byte-identical
    // output to the pre-orientation primitive (Files/Documents must not regress).
    orientation: {
      horizontal: '',
      vertical: 'flex-col w-full',
    },
  },
  defaultVariants: { variant: 'default', orientation: 'horizontal' },
});

const tabsTriggerVariants = cva(
  'focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap focus-visible:ring-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'data-[state=active]:bg-background data-[state=active]:text-foreground rounded-md px-3 py-1 text-sm font-medium transition-all data-[state=active]:shadow',
        boxed:
          'flex-1 gap-2 rounded-md border px-4 py-2 font-bold transition-colors duration-200 border-chariot-stroke text-chariot-text-secondary hover:border-chariot-text-primary hover:text-chariot-text-primary data-[state=active]:border-chariot-text-primary data-[state=active]:bg-chariot-background-secondary data-[state=active]:text-chariot-text-primary',
      },
      // `horizontal` is a no-op (byte-identical output when omitted); `vertical`
      // makes each trigger a full-width, left-justified row for a side-rail list.
      orientation: {
        horizontal: '',
        vertical: 'w-full justify-start',
      },
    },
    defaultVariants: { variant: 'default', orientation: 'horizontal' },
  }
);

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
    VariantProps<typeof tabsListVariants>
>(({ className, variant = 'default', orientation, ...props }, ref) => (
  // `orientation` drives only the cva class here — the Radix `data-orientation`
  // attribute is derived from the `<Tabs orientation>` Root context, so we do
  // NOT forward it onto `TabsPrimitive.List` (it is not a Radix List prop).
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant, orientation }), className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, variant = 'default', orientation, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant, orientation }), className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:outline-hidden',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
